import { type CvssScore } from './cvss-score';
import { MultiCvssVectorParser } from './multi-cvss-vector-parser';
import { Cvss2VectorParser } from './v2/cvss2-vector-parser';
import {
    Cvss2VectorPrefixOption,
    Cvss2VectorRenderer,
} from './v2/cvss2-vector-renderer';
import { Cvss2VectorMocker } from './v2/cvss2-vector-mocker';
import { Cvss3VectorParser } from './v3/cvss3-vector-parser';
import {
    Cvss3VectorPrefixOption,
    Cvss3VectorRenderer,
} from './v3/cvss3-vector-renderer';
import { Cvss3VectorMocker } from './v3/cvss3-vector-mocker';

// export utils
export { roundUp } from './utils/cvss-utils';

// Export enums.
export { CvssVersion } from './cvss-enums';
export { enums as cvss2 } from './v2/cvss2-enums';
export { enums as cvss3 } from './v3/cvss3-enums';

// Export classes (score object, scoring engines and parsers).
export { CvssScore } from './cvss-score';
export { Cvss40Score } from './v4/cvss40-score';
export { MultiCvssVectorParser } from './multi-cvss-vector-parser';
export { Cvss2ScoringEngine } from './v2/cvss2-scoring-engine';
export { Cvss2VectorParser } from './v2/cvss2-vector-parser';
export { Cvss2VectorMocker } from './v2/cvss2-vector-mocker';
export {
    Cvss2VectorPrefixOption,
    Cvss2VectorRenderer,
} from './v2/cvss2-vector-renderer';
export { Cvss3ScoringEngine } from './v3/cvss3-scoring-engine';
export { Cvss3VectorParser } from './v3/cvss3-vector-parser';
export { Cvss3VectorMocker } from './v3/cvss3-vector-mocker';
export {
    Cvss3VectorPrefixOption,
    Cvss3VectorRenderer,
} from './v3/cvss3-vector-renderer';

/**
 * Parses a CVSS v2 vector and returns the resulting score object.
 *
 * @param vector - the vector to parse
 * @returns the resulting score object
 */
export function parseCvss2Vector(vector: string): CvssScore {
    const cvss2VectorParser = new Cvss2VectorParser();
    return cvss2VectorParser.parse(vector);
}

/**
 * Parses a CVSS v3 vector and returns the resulting score object.
 *
 * @param vector - the vector to parse
 * @returns the resulting score object
 */
export function parseCvss3Vector(vector: string): CvssScore {
    const cvss3VectorParser = new Cvss3VectorParser();
    return cvss3VectorParser.parse(vector);
}

/**
 * Parses a CVSS vector (any version) and returns the resulting score object.
 *
 * @param vector - the vector to parse
 * @returns the resulting score object
 */
export function parseCvssVector(vector: string): CvssScore {
    const parser = new MultiCvssVectorParser();
    return parser.parse(vector);
}

/*
 * Reorders a CVSS vector (any version) to ensure that the keys are in the correct order.
 * This is useful for ensuring consistent representation of CVSS vectors.
 *
 * @param vector - the vector to reorder
 * @returns the reordered vector as a string
 */
export function reorderVector(vector: string): string {
    const parser = new MultiCvssVectorParser();
    return parser.reorderVector(vector);
}

/**
 * Parse a Cvss vector (any version) and if successful return the resulting score object.
 * if unsuccessful it returns an Error with detailed information about the problems.
 * @param vector - the vector to parse
 */
export function safeParseCvssVector(
    vector: string,
): { success: true; score: CvssScore } | { success: false; error: Error } {
    const parser = new MultiCvssVectorParser();
    return parser.safeParse(vector);
}

/**
 * Validates a CVSS v2 vector.
 *
 * @param vector - the vector to parse
 * @returns true if validation succeeded, otherwise false
 */
export function validateCvss2Vector(vector: string): boolean {
    try {
        parseCvss2Vector(vector);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Validates a CVSS v3.x vector.
 *
 * @param vector - the vector to parse
 * @returns true if validation succeeded, otherwise false
 */
export function validateCvss3Vector(vector: string): boolean {
    try {
        parseCvss3Vector(vector);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 *  Parses a CVSS vector (any version).
 *
 * @param vector - the vector to parse
 * @returns true if validation succeeded, otherwise false
 */
export function validateCvssVector(vector: string): boolean {
    try {
        parseCvssVector(vector);
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * Renders and returns a random CVSS v2 vector as a string.
 *
 * @param includeTemporal - whether or not to include a temporal score on the vector
 * @param includeEnvironmental - whether or not to include an environmental score on the vector
 * @param prefixOption - the desired vector prefixing option
 * @returns the vector as a string
 */
export function randomCvss2Vector(
    includeTemporal = false,
    includeEnvironmental = false,
    prefixOption: Cvss2VectorPrefixOption = Cvss2VectorPrefixOption.Version,
): string {
    const randomizer = new Cvss2VectorMocker(
        includeTemporal,
        includeEnvironmental,
    );
    const renderer = new Cvss2VectorRenderer(prefixOption);
    return renderer.render(randomizer.generate());
}

/**
 * Renders and returns a random CVSS v3 vector as a string.
 *
 * @param includeTemporal - whether or not to include a temporal score on the vector
 * @param includeEnvironmental - whether or not to include an environmental score on the vector
 * @param prefixOption - the desired vector prefixing option
 * @returns the vector as a string
 */
export function randomCvss3Vector(
    includeTemporal = false,
    includeEnvironmental = false,
    prefixOption: Cvss3VectorPrefixOption = Cvss3VectorPrefixOption.Version31,
): string {
    const randomizer = new Cvss3VectorMocker(
        includeTemporal,
        includeEnvironmental,
    );
    const renderer = new Cvss3VectorRenderer(prefixOption);
    return renderer.render(randomizer.generate());
}
